import { BoxGeometry, CylinderGeometry, Group, Mesh } from 'three';
import type { BalconyConfig, HouseBodyConfig } from '@/model/schema';
import type { MaterialKit } from '../materials';

/**
 * A small protruding balcony attached to one facade of a non-ground story.
 *
 * The host story takes care of the door (it's added to story.doors by the
 * extras generator); this builder only emits the geometry that lives
 * outside the house body:
 *   - A thin floor slab at the host story's floor elevation.
 *   - A railing wrapping the three open edges (left, far, right).
 *
 * Coordinates use the same facade convention as the deck: `width` along
 * the facade, `depth` extends outward.
 */

const BALCONY_SLAB_THICKNESS = 0.12;
const BALCONY_RAILING_HEIGHT = 1.0;
const BALCONY_RAILING_POST_RADIUS = 0.035;
const BALCONY_RAILING_POST_SPACING = 0.7;

export function buildBalcony(
  body: HouseBodyConfig,
  balcony: BalconyConfig,
  floorTopY: number,
  kit: MaterialKit,
): Group {
  const group = new Group();
  group.name = 'Balcony';

  const facadeAxis = balcony.side === 'front' || balcony.side === 'back' ? 'x' : 'z';
  const sx = facadeAxis === 'x' ? balcony.width : balcony.depth;
  const sz = facadeAxis === 'x' ? balcony.depth : balcony.width;

  // Slab top sits exactly on the indoor floor top so the doorway
  // transition is flush, no step. We add 1mm of separation below to
  // avoid coinciding with the upper-story slab's underside.
  const slabY = floorTopY - BALCONY_SLAB_THICKNESS / 2 - 0.001;
  const { centerX, centerZ } = computeBalconyCenter(body, balcony, sx, sz);

  const slab = new Mesh(
    new BoxGeometry(sx, BALCONY_SLAB_THICKNESS, sz),
    kit.basePlate,
  );
  slab.name = 'BalconySlab';
  slab.castShadow = true;
  slab.receiveShadow = true;
  slab.position.set(centerX, slabY, centerZ);
  group.add(slab);

  if (balcony.hasRailing) {
    addBalconyRailing(group, body, balcony, kit, slabY + BALCONY_SLAB_THICKNESS / 2);
  }

  return group;
}

function computeBalconyCenter(
  body: HouseBodyConfig,
  balcony: BalconyConfig,
  sx: number,
  sz: number,
) {
  switch (balcony.side) {
    case 'front':
      return {
        centerX: body.originX + balcony.offset + sx / 2,
        centerZ: body.originZ - sz / 2,
      };
    case 'back':
      return {
        centerX: body.originX + body.footprintWidth - balcony.offset - sx / 2,
        centerZ: body.originZ + body.footprintDepth + sz / 2,
      };
    case 'left':
      return {
        centerX: body.originX - sx / 2,
        centerZ: body.originZ + body.footprintDepth - balcony.offset - sz / 2,
      };
    case 'right':
      return {
        centerX: body.originX + body.footprintWidth + sx / 2,
        centerZ: body.originZ + balcony.offset + sz / 2,
      };
  }
}

function balconyAnchor(body: HouseBodyConfig, balcony: BalconyConfig) {
  switch (balcony.side) {
    case 'front':
      return {
        centerX: body.originX + balcony.offset + balcony.width / 2,
        centerZ: body.originZ,
        rotY: Math.PI,
      };
    case 'back':
      return {
        centerX: body.originX + body.footprintWidth - balcony.offset - balcony.width / 2,
        centerZ: body.originZ + body.footprintDepth,
        rotY: 0,
      };
    case 'left':
      return {
        centerX: body.originX,
        centerZ: body.originZ + body.footprintDepth - balcony.offset - balcony.width / 2,
        rotY: -Math.PI / 2,
      };
    case 'right':
      return {
        centerX: body.originX + body.footprintWidth,
        centerZ: body.originZ + balcony.offset + balcony.width / 2,
        rotY: Math.PI / 2,
      };
  }
}

function addBalconyRailing(
  group: Group,
  body: HouseBodyConfig,
  balcony: BalconyConfig,
  kit: MaterialKit,
  topY: number,
): void {
  const local = new Group();
  local.name = 'BalconyRailing';

  const w = balcony.width;
  const d = balcony.depth;

  // Far edge.
  addRail(local, kit, -w / 2, w / 2, d, true, topY);
  // Left end.
  addRail(local, kit, -w / 2, -w / 2, d, false, topY);
  // Right end.
  addRail(local, kit, w / 2, w / 2, d, false, topY);

  const a = balconyAnchor(body, balcony);
  local.position.set(a.centerX, 0, a.centerZ);
  local.rotation.y = a.rotY;
  group.add(local);
}

function addRail(
  parent: Group,
  kit: MaterialKit,
  x0: number,
  x1: number,
  zPos: number,
  alongX: boolean,
  topY: number,
): void {
  const length = alongX ? Math.abs(x1 - x0) : zPos;

  const cx = alongX ? (x0 + x1) / 2 : x0;
  const cz = alongX ? zPos : zPos / 2;

  const railGeo = new BoxGeometry(
    alongX ? length : 0.04,
    0.04,
    alongX ? 0.04 : length,
  );
  const rail = new Mesh(railGeo, kit.fence);
  rail.castShadow = true;
  rail.receiveShadow = true;
  rail.position.set(cx, topY + BALCONY_RAILING_HEIGHT, cz);
  parent.add(rail);

  const totalLen = length;
  const postCount = Math.max(2, Math.ceil(totalLen / BALCONY_RAILING_POST_SPACING) + 1);
  for (let i = 0; i < postCount; i++) {
    const t = postCount === 1 ? 0.5 : i / (postCount - 1);
    const px = alongX ? x0 + (x1 - x0) * t : x0;
    const pz = alongX ? zPos : t * length;
    const post = new Mesh(
      new CylinderGeometry(
        BALCONY_RAILING_POST_RADIUS,
        BALCONY_RAILING_POST_RADIUS,
        BALCONY_RAILING_HEIGHT,
        8,
      ),
      kit.fence,
    );
    post.castShadow = true;
    post.receiveShadow = true;
    post.position.set(px, topY + BALCONY_RAILING_HEIGHT / 2, pz);
    parent.add(post);
  }
}
